<template>
	<glass-card ref="formTopRef" class="add-meal-container mt-3 mb-5">
		<header class="add-header">
			<div>
				<h1>Add meal</h1>
			</div>
			<button class="btn btn-primary back-btn" type="button" @click="handleBack">
				<i class="bi bi-arrow-left"></i>
				Back
			</button>
		</header>

		<nav class="stepper" aria-label="Add meal progress">
			<button
				v-for="step in steps"
				:key="step.id"
				type="button"
				class="step"
				:class="{ active: activeStep === step.id, complete: isStepComplete(step.id) }"
				@click="goToStep(step.id)">
				<span class="step-index">
					<i v-if="isStepComplete(step.id)" class="bi bi-check"></i>
					<span v-else>{{ step.index }}</span>
				</span>
				<span>{{ step.label }}</span>
			</button>
		</nav>

		<form id="add-meal-form" @submit.prevent="submitMeal" class="add-meal-form">
			<section v-if="activeStep === 'capture'" class="flow-section">
				<div class="section-heading">
					<h2>Start with what you have</h2>
					<p>Use a photo when possible, or describe the meal if there is no image.</p>
				</div>

				<div class="capture-panel">
					<image-upload
						ref="imageUploadRef"
						class="capture-upload"
						v-model="imagePreview"
						enable-targeting
						@targetChange="handleImageTarget"
						@targetEditorOpen="targetEditorOpen = $event" />
				</div>

				<div
					v-if="addScreenPreview && !targetEditorOpen"
					class="image-preview"
					:class="{ 'target-preview': imageTarget?.targetImageSrc }">
					<img
						:src="addScreenPreview"
						alt="Meal preview"
						class="img-thumbnail"
						:class="{ 'target-preview-img': imageTarget?.targetImageSrc }" />
					<div class="preview-footer">
						<div class="target-status">
							<i class="bi bi-crosshair"></i>
							{{ imageTarget?.targetMode ? `Target: ${targetLabel}` : "Full photo" }}
						</div>
						<div class="target-actions">
							<button
								type="button"
								class="btn btn-sm btn-outline-light icon-btn"
								title="How targeting works"
								aria-label="How targeting works"
								@click="showTargetHelp">
								<i class="bi bi-question-circle"></i>
							</button>
							<button
								type="button"
								class="btn btn-sm btn-outline-warning"
								@click="changeTarget">
								<i class="bi bi-pencil-square"></i>
								{{ imageTarget?.targetMode ? "Change target" : "Target item" }}
							</button>
						</div>
					</div>
				</div>

				<div class="mb-4">
					<label for="Prompt" class="form-label">Description or extra notes</label>
					<textarea
						id="Prompt"
						class="form-control"
						v-model="prompt"
						rows="4"
						placeholder="Example: chicken burrito bowl with rice, beans, salsa, and sour cream"></textarea>
				</div>

				<div class="flow-actions">
					<button
						type="button"
						class="btn btn-success btn-lg"
						:disabled="!hasCapture"
						@click="setActiveStep('context')">
						Continue
						<i class="bi bi-arrow-right"></i>
					</button>
				</div>
			</section>

			<section v-else-if="activeStep === 'context'" class="flow-section">
				<div class="section-heading">
					<h2>Add context</h2>
					<p>These details help the queue process the meal correctly.</p>
				</div>

				<div class="context-controls">
					<div class="control-group">
						<label class="form-label">Meal type</label>
						<div class="meal-type-grid" role="group" aria-label="Meal type">
							<button
								v-for="option in mealTypeOptions"
								:key="option.value"
								type="button"
								class="choice-chip"
								:class="{ selected: mealType === option.value }"
								@click="mealType = option.value">
								{{ option.label }}
							</button>
						</div>
					</div>

					<div class="control-group">
						<label class="form-label">Cook type</label>
						<div class="cook-type-grid" role="group" aria-label="Cook type">
							<button
								v-for="option in cookTypeOptions"
								:key="option.value"
								type="button"
								class="cook-card"
								:class="{ selected: cookType === option.value }"
								@click="selectCookType(option.value)">
								<i class="bi" :class="option.icon"></i>
								<span>{{ option.label }}</span>
							</button>
						</div>
					</div>

					<div v-if="cookType === 'restaurant'" class="control-group">
						<restaurant-selector
							v-model="restaurantName"
							:transparent="true"
							@customRestaurant="handleCustomRestaurant" />
					</div>
				</div>

				<div class="mb-3">
					<label for="mealDateTime" class="form-label">Date and Time</label>
					<input
						type="datetime-local"
						class="form-control"
						id="mealDateTime"
						v-model="mealDateTime" />
				</div>

				<div class="flow-actions split-actions">
					<button type="button" class="btn btn-outline-light" @click="setActiveStep('capture')">
						<i class="bi bi-arrow-left"></i>
						Capture
					</button>
					<button type="button" class="btn btn-success btn-lg" @click="setActiveStep('review')">
						Review
						<i class="bi bi-arrow-right"></i>
					</button>
				</div>
			</section>

			<section v-else class="flow-section">
				<div class="section-heading">
					<h2>Ready for the queue</h2>
					<p>The meal will show under Processing Items while it sends and analyzes.</p>
				</div>

				<div class="review-card">
					<div class="review-preview">
						<img v-if="addScreenPreview" :src="addScreenPreview" alt="Meal preview" />
						<div v-else class="text-preview">
							<i class="bi bi-card-text"></i>
							<span>{{ descriptionSummary }}</span>
						</div>
					</div>

					<div class="review-list">
						<div class="review-row">
							<span>Meal type</span>
							<strong>{{ displayValue(mealType) }}</strong>
						</div>
						<div class="review-row">
							<span>Cook type</span>
							<strong>{{ displayValue(cookType) }}</strong>
						</div>
						<div v-if="restaurantName" class="review-row">
							<span>Restaurant</span>
							<strong>{{ restaurantName }}</strong>
						</div>
						<div class="review-row">
							<span>Date and time</span>
							<strong>{{ formattedMealDateTime }}</strong>
						</div>
						<div v-if="imageTarget?.targetMode" class="review-row">
							<span>Photo target</span>
							<strong>{{ targetLabel }}</strong>
						</div>
					</div>
				</div>

				<div class="queue-note">
					<i class="bi bi-list-check"></i>
					<span>After adding, you will return to the dashboard and see this item in the processing queue.</span>
				</div>

				<div class="flow-actions split-actions">
					<button type="button" class="btn btn-outline-light" @click="setActiveStep('context')">
						<i class="bi bi-arrow-left"></i>
						Context
					</button>
					<button type="submit" class="btn btn-success btn-lg submit-meal-btn">
						<i class="bi bi-plus-circle"></i>
						Add to queue
					</button>
				</div>
			</section>
		</form>
	</glass-card>

	<nav
		v-if="!targetEditorOpen"
		class="mobile-flow-actions"
		aria-label="Add meal actions">
		<template v-if="activeStep === 'capture'">
			<button
				type="button"
				class="btn btn-success btn-lg"
				:disabled="!hasCapture"
				@click="setActiveStep('context')">
				Continue
				<i class="bi bi-arrow-right"></i>
			</button>
		</template>
		<template v-else-if="activeStep === 'context'">
			<button type="button" class="btn btn-outline-light" @click="setActiveStep('capture')">
				<i class="bi bi-arrow-left"></i>
				Capture
			</button>
			<button type="button" class="btn btn-success btn-lg" @click="setActiveStep('review')">
				Review
				<i class="bi bi-arrow-right"></i>
			</button>
		</template>
		<template v-else>
			<button type="button" class="btn btn-outline-light" @click="setActiveStep('context')">
				<i class="bi bi-arrow-left"></i>
				Context
			</button>
			<button type="submit" form="add-meal-form" class="btn btn-success btn-lg submit-meal-btn">
				<i class="bi bi-plus-circle"></i>
				Add to queue
			</button>
		</template>
	</nav>
</template>

<script setup>
	import { computed, nextTick, ref, onBeforeUnmount, watch } from "vue"
	import Swal from "sweetalert2"
	import { useRoute, useRouter } from "vue-router"
	import RestaurantSelector from "@/components/RestaurantSelector.vue"
	import { useItemQueueStore } from "@/stores/item-queue"
	import { showAd, upgradeAd } from "@/utils/adBus"
	import ImageUpload from "@/components/ImageUpload.vue"
	import { bloodSugarNotif } from "@/utils/notif"
	import checkNewRestaurant from "@/utils/newRestaurant"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"
	import { Capacitor } from "@capacitor/core"
	import { useUserStore } from "@/stores/user"
	import GlassCard from "@/components/GlassCard.vue"
	import EventBus from "@/utils/eventBus"

	const iqStore = useItemQueueStore()
	const router = useRouter()
	const route = useRoute()
	const userStore = useUserStore()

	const currentDate = new Date()
	const timezoneOffset = currentDate.getTimezoneOffset() * 60000
	const localISOTime = new Date(currentDate - timezoneOffset)
		.toISOString()
		.slice(0, 16)

	const steps = [
		{ id: "capture", label: "Capture", index: 1 },
		{ id: "context", label: "Context", index: 2 },
		{ id: "review", label: "Queue", index: 3 },
	]

	const mealTypeOptions = [
		{ value: "breakfast", label: "Breakfast" },
		{ value: "lunch", label: "Lunch" },
		{ value: "dinner", label: "Dinner" },
		{ value: "snack", label: "Snack" },
	]

	const cookTypeOptions = [
		{ value: "home-cooked", label: "Home", icon: "bi-house-door" },
		{ value: "restaurant", label: "Restaurant", icon: "bi-shop" },
	]

	function autoMealType() {
		const hour = new Date().getHours()
		if (hour >= 5 && hour < 11) return "breakfast"
		if (hour >= 11 && hour < 16) return "lunch"
		if (hour >= 16 && hour < 22) return "dinner"
		return "snack"
	}

	function requestedMealType() {
		const requested = Array.isArray(route.query.mealType)
			? route.query.mealType[0]
			: route.query.mealType
		return mealTypeOptions.some((option) => option.value === requested)
			? requested
			: null
	}

	const activeStep = ref("capture")
	const mealDateTime = ref(localISOTime)
	const cookType = ref("home-cooked")
	const mealType = ref(requestedMealType() ?? autoMealType())
	const prompt = ref("")
	const restaurantName = ref("")

	const customRestaurantData = ref(null)
	const imagePreview = ref(null)
	const imageTarget = ref(null)
	const imageUploadRef = ref(null)
	const formTopRef = ref(null)
	const targetEditorOpen = ref(false)

	watch(
		() => route.query.mealType,
		() => {
			const requested = requestedMealType()
			if (requested) mealType.value = requested
		}
	)

	const addScreenPreview = computed(() => {
		return imageTarget.value?.targetImageSrc || imagePreview.value
	})

	const trimmedPrompt = computed(() => prompt.value?.trim() || "")

	const hasCapture = computed(() => {
		return !!imagePreview.value || !!trimmedPrompt.value
	})

	const descriptionSummary = computed(() => {
		return trimmedPrompt.value || "No description"
	})

	const formattedMealDateTime = computed(() => {
		if (!mealDateTime.value) return "Now"
		return new Date(mealDateTime.value).toLocaleString([], {
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
		})
	})

	const targetLabel = computed(() => {
		switch (imageTarget.value?.targetMode) {
			case "tap-mask":
				return "tap mask"
			case "draw-mask":
				return "drawn mask"
			case "box":
				return "box"
			case "draw-circle":
				return "drawn circle"
			case "multi-target":
				return "multiple targets"
			default:
				return "full photo"
		}
	})

	function displayValue(value) {
		if (!value || value === "default") return "Auto"
		return value
			.split("-")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ")
	}

	function isStepComplete(stepId) {
		if (stepId === "capture") return hasCapture.value
		if (stepId === "context") return hasCapture.value && activeStep.value === "review"
		return false
	}

	function goToStep(stepId) {
		if (stepId !== "capture" && !hasCapture.value) {
			Swal.fire({
				icon: "info",
				title: "Add a photo or description first.",
			})
			return
		}
		setActiveStep(stepId)
	}

	async function setActiveStep(stepId) {
		activeStep.value = stepId
		await nextTick()
		const target = formTopRef.value?.$el || formTopRef.value
		target?.scrollIntoView?.({
			behavior: "smooth",
			block: "start",
		})
	}

	function handleCustomRestaurant(data) {
		customRestaurantData.value = data
	}

	function selectCookType(value) {
		cookType.value = value
		if (value !== "restaurant") {
			restaurantName.value = ""
			customRestaurantData.value = null
		}
	}

	function handleImageTarget(target) {
		imageTarget.value = target
		imagePreview.value = target?.imageSrc ?? null
	}

	function changeTarget() {
		imageUploadRef.value?.editTarget(imageTarget.value?.imageSrc || imagePreview.value)
	}

	function showTargetHelp() {
		Swal.fire({
			icon: "info",
			title: "Targeting a photo",
			html: `
				<div class="text-start">
					<p>The full photo is used by default. You only need targeting when you want the AI to focus on one item or a few items.</p>
					<p>Choose <strong>Target item</strong>, then tap, draw a mask, draw a box, or draw a circle around what should be identified.</p>
					<p>You can select multiple areas. Use <strong>Erase</strong> and tap a highlighted area to remove it, or <strong>Undo</strong> to step back.</p>
					<p><strong>Cancel</strong> keeps the current photo/target. <strong>Use target</strong> applies the selected target preview.</p>
				</div>
			`,
			confirmButtonText: "Got it",
		})
	}

	const submitMeal = async () => {
		if (!hasCapture.value) {
			Swal.fire({
				icon: "error",
				title: "Please provide an image or a description.",
			})
			activeStep.value = "capture"
			return
		}

		await checkNewRestaurant(cookType.value, customRestaurantData.value)

		const requestId = crypto.randomUUID()
		const tempId = `tempId-${requestId}`
		const newItem = {
			id: tempId,
			requestId,
			status: "sending",
			mealDateTime: mealDateTime.value,
			cookType: cookType.value,
			mealType: mealType.value,
			prompt: trimmedPrompt.value || null,
			restaurantName: restaurantName.value,
			imageSrc: imagePreview.value,
			targetImageSrc: imageTarget.value?.targetImageSrc ?? null,
			targetMode: imageTarget.value?.targetMode ?? null,
			targetRegion: imageTarget.value?.targetRegion ?? null,
		}

		await iqStore.load()
		await iqStore.add(newItem)
		if (!userStore.isViewingUser()) {
			EventBus.emit("meal-entry-queued", {
				mealType: mealType.value,
				mealDateTime: mealDateTime.value,
			})
		}

		if (!SubscriptionHelper.isFreeAccount()) {
			if (userStore.user?.bloodSugarNotifications && mealType.value) {
				await bloodSugarNotif(
					mealType.value,
					userStore.user?.bloodSugarNotificationDelay,
					mealDateTime.value
				)
			}
		}

		imagePreview.value = null
		imageTarget.value = null

		if (SubscriptionHelper.isFreeAccount()) {
			if (Capacitor.isNativePlatform()) {
				showAd.value = true
			} else {
				upgradeAd.value = true
			}
		}

		router.push({ name: "dashboard" })
	}

	function handleBack() {
		router.push({ name: "dashboard" })
	}

	onBeforeUnmount(() => {
		if (imagePreview.value) {
			imagePreview.value = null
		}
		imageTarget.value = null
	})
</script>

<style lang="less" scoped>
	.add-meal-container {
		max-width: 680px;
		margin: auto;
		padding: 1rem;
	}

	.add-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
		margin-bottom: 1.25rem;

		h1 {
			margin: 0;
			color: #ff6400;
			font-size: 2.35rem;
			line-height: 1;
		}
	}

	.back-btn {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		white-space: nowrap;
	}

	.stepper {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 0.55rem;
		margin-bottom: 1.25rem;
	}

	.step {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.45rem;
		min-height: 44px;
		padding: 0.45rem 0.55rem;
		border: 1px solid rgba(255, 255, 255, 0.18);
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.04);
		color: #cbd5e1;
		font-weight: 800;

		&.active {
			border-color: #22c55e;
			color: #ffffff;
			background: rgba(34, 197, 94, 0.14);
		}

		&.complete {
			border-color: rgba(34, 197, 94, 0.8);
			color: #bbf7d0;
		}
	}

	.step-index {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.45rem;
		height: 1.45rem;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.12);
		font-size: 0.85rem;
	}

	.flow-section {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.section-heading {
		h2 {
			margin: 0 0 0.25rem;
			font-size: 1.45rem;
			color: #ffffff;
		}

		p {
			margin: 0;
			color: #cbd5e1;
		}
	}

	.capture-panel {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.capture-upload {
		flex: 1 1 100%;
		min-width: 0;
	}

	.capture-panel :deep(.image-upload-buttons) {
		margin-bottom: 0 !important;
	}

	.capture-panel :deep(.image-upload-buttons .btn) {
		min-height: 46px;
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		border-radius: 8px;
		font-weight: 800;
	}

	.image-preview {
		padding: 0.75rem;
		border: 1px solid rgba(255, 255, 255, 0.16);
		border-radius: 8px;
		background: rgba(2, 6, 23, 0.22);

		img {
			display: block;
			width: 100%;
			max-height: 360px;
			object-fit: contain;
			border-radius: 8px;
		}
	}

	.image-preview.target-preview {
		background-color: #142829;
		background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.16) 25%, transparent 25%),
			linear-gradient(-45deg, rgba(255, 255, 255, 0.16) 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.16) 75%),
			linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.16) 75%);
		background-position: 0 0, 0 8px, 8px -8px, -8px 0;
		background-size: 16px 16px;
	}

	.image-preview .target-preview-img {
		background-color: transparent;
		border-color: rgba(255, 255, 255, 0.6);
	}

	.preview-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 0.75rem;
	}

	.target-status,
	.queue-note {
		display: inline-flex;
		align-items: center;
		gap: 0.45rem;
		padding: 0.45rem 0.65rem;
		border-radius: 8px;
		background: rgba(34, 197, 94, 0.16);
		color: #bbf7d0;
		font-weight: 800;
	}

	.target-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}

	.icon-btn {
		width: 36px;
		justify-content: center;
	}

	.context-preview,
	.review-preview {
		border-radius: 8px;
		overflow: hidden;
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid rgba(255, 255, 255, 0.12);

		img {
			display: block;
			width: 100%;
			max-height: 220px;
			object-fit: cover;
		}
	}

	.context-controls {
		display: grid;
		gap: 1.15rem;
	}

	.control-group {
		display: grid;
		gap: 0.55rem;
	}

	.meal-type-grid,
	.cook-type-grid {
		display: grid;
		gap: 0.55rem;
	}

	.meal-type-grid {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	.cook-type-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.choice-chip,
	.cook-card {
		border: 1px solid rgba(255, 255, 255, 0.22);
		border-radius: 8px;
		background: rgba(15, 23, 42, 0.25);
		color: #ffffff;
		font-weight: 800;
		transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
	}

	.choice-chip {
		min-height: 42px;
		padding: 0.45rem 0.6rem;
	}

	.cook-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.35rem;
		min-height: 78px;
		padding: 0.7rem 0.5rem;

		i {
			font-size: 1.45rem;
			color: #cbd5e1;
		}
	}

	.choice-chip:hover,
	.cook-card:hover {
		border-color: rgba(34, 197, 94, 0.65);
		background: rgba(34, 197, 94, 0.1);
	}

	.choice-chip.selected,
	.cook-card.selected {
		border-color: #22c55e;
		background: rgba(34, 197, 94, 0.2);
		box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.45);
		color: #ffffff;

		i {
			color: #bbf7d0;
		}
	}

	.context-controls :deep(.restaurant-section .form-label) {
		color: #ff6400;
		font-weight: 800;
	}

	.text-preview {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		min-height: 84px;
		padding: 1rem;
		color: #ffffff;
		font-weight: 800;
		word-break: break-word;

		i {
			color: #fdaf70;
			font-size: 1.4rem;
		}
	}

	.review-card {
		display: grid;
		grid-template-columns: minmax(0, 0.8fr) minmax(0, 1fr);
		gap: 1rem;
		align-items: stretch;
	}

	.review-list {
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 8px;
		overflow: hidden;
	}

	.review-row {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.85rem 0.95rem;
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);

		&:last-child {
			border-bottom: 0;
		}

		span {
			color: #cbd5e1;
		}

		strong {
			color: #ffffff;
			text-align: right;
		}
	}

	.queue-note {
		align-items: flex-start;
		line-height: 1.35;
	}

	.flow-actions {
		display: flex;
		justify-content: flex-end;
		margin-top: 0.5rem;

		.btn {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 0.5rem;
			border-radius: 8px;
			font-weight: 800;
		}
	}

	.split-actions {
		justify-content: space-between;
		gap: 1rem;
	}

	.submit-meal-btn {
		padding: 0.85rem 1.4rem;
	}

	.mobile-flow-actions {
		display: none;
	}

	.form-label {
		color: #ff6400;
		font-weight: 800;
	}

	.form-control {
		background-color: transparent !important;
		color: #ffffff !important;
		border-color: rgba(255, 255, 255, 0.25);
	}

	.form-control:focus {
		background-color: transparent !important;
		color: #ffffff !important;
		box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
		border-color: #22c55e;
	}

	.form-control::placeholder {
		color: #94a3b8;
		opacity: 0.8;
	}

	input[type="datetime-local"]::-webkit-calendar-picker-indicator {
		filter: invert(1) brightness(1.6);
		opacity: 0.9;
	}

	@media (max-width: 767.98px), (hover: none) and (pointer: coarse) {
		.add-meal-container {
			padding-bottom: calc(6.25rem + env(safe-area-inset-bottom, 0px));
		}

		.flow-actions {
			display: none;
		}

		.mobile-flow-actions {
			position: fixed;
			z-index: 30;
			left: 50%;
			bottom: 0;
			display: flex;
			justify-content: flex-end;
			gap: 0.75rem;
			width: min(680px, 100%);
			padding: 0.75rem max(0.85rem, env(safe-area-inset-right, 0px))
				max(0.75rem, env(safe-area-inset-bottom, 0px))
				max(0.85rem, env(safe-area-inset-left, 0px));
			border: 1px solid rgba(255, 255, 255, 0.16);
			border-bottom: 0;
			border-radius: 1rem 1rem 0 0;
			background: rgba(4, 17, 15, 0.94);
			box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.34);
			backdrop-filter: blur(18px) saturate(140%);
			-webkit-backdrop-filter: blur(18px) saturate(140%);
			transform: translateX(-50%);

			.btn {
				display: inline-flex;
				flex: 1 1 0;
				align-items: center;
				justify-content: center;
				gap: 0.5rem;
				min-width: 0;
				min-height: 48px;
				border-radius: 8px;
				font-weight: 800;
			}
		}
	}

	@media (max-width: 640px) {
		.add-meal-container {
			padding: 0.85rem;
			padding-bottom: calc(6.25rem + env(safe-area-inset-bottom, 0px));
		}

		.add-header {
			align-items: stretch;
			flex-direction: column;
		}

		.back-btn {
			align-self: flex-start;
		}

		.step {
			flex-direction: column;
			gap: 0.25rem;
			font-size: 0.85rem;
		}

		.meal-type-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.cook-type-grid {
			grid-template-columns: 1fr;
		}

		.review-card {
			grid-template-columns: 1fr;
		}
	}
</style>
